import React from 'react';
import { DeleteOutlined } from "@ant-design/icons";
import { notification } from "antd";
import { formatNumberAsCurrency } from "../../utils/helpers";
import { useAuthStore } from "../../store/auth/authStore";
import CreditBalIcon from "../../assets/images/credit-bal-icon.svg";
import { useFlightSearchStore } from "../../store/flightSearch/flightSearchStore";
import { calculateFlightTotalFare } from "../AvailableFlights/helper";
import { useBookingStore } from "../../store/booking/bookingStore";
import { calculateTicketTotal } from "../AvailableFlights/helper";
import { getAPI } from "../../utils/api";
import { APPLY_CREDIT } from "../../utils/constants";

import {
    CreditBannerWrapper,
    CreditBannerContent,
    CreditBalanceLabel,
    CreditBalanceAmount,
    CreditBannerBackground,
    StyledCreditButton,
} from "./styles";
import styled from "styled-components";
import { PRIMARY } from "../../utils/colors";

export const RedeemCreditAppliedRow = styled.div`
  background: #FFF8ED;
  padding: 12px 16px;
  color: ${PRIMARY};
  font-size: 13px;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #F3E2C8;
`;

interface MobileCreditBannerProps {
    promoCodeEntered?: boolean;
}

const MobileCreditBanner = ({ promoCodeEntered }: MobileCreditBannerProps) => {
    const { 
        isLoggedIn, 
        b2CUser, 
        creditApplied, 
        creditLoading, 
        creditAmount, 
        setCreditLoading, 
        setCreditAppliedDetails, 
        clearCreditDetails, 
        removeCredit 
    } = useAuthStore();
    const { selectedFlight, formData: { isDomestic } } = useFlightSearchStore();
    const { bookingPayload } = useBookingStore();

    if (!isLoggedIn || !b2CUser?.WalletBalance || b2CUser.WalletBalance <= 0) return null;

    const { totalTicketAmount } = calculateFlightTotalFare(selectedFlight, isDomestic);
    const bookingAmount = bookingPayload
        ? calculateTicketTotal(bookingPayload.TicketingDetails)
        : totalTicketAmount;

    let creditDeducted = 0;
    if (creditApplied && creditAmount > 0) {
        creditDeducted = Math.min(creditAmount, bookingAmount);
    }

    const handleApplyCredit = async () => {
        setCreditLoading(true);
        try {
            const response = await getAPI(APPLY_CREDIT);
            setCreditLoading(false);

            if (response.status === 200 && response.data?.IsSuccess) {
                const responseType = response.data.ResponseType;
                
                if (responseType.HasUsedDiscount) {
                    clearCreditDetails();
                    notification.error({
                        message: 'Credit discount has already been used.',
                        placement: 'bottom',
                    });
                } else {
                    setCreditAppliedDetails(responseType);
                    notification.success({
                        message: 'Credit applied successfully',
                        placement: 'bottom',
                    });
                }
            } else {
                const errorMsg = response.data?.ErrorMessageList?.[0] || 'Failed to apply credit.';
                clearCreditDetails();
                notification.error({
                    message: errorMsg,
                    placement: 'bottom',
                });
            }
        } catch (error) {
            setCreditLoading(false);
            clearCreditDetails();
            notification.error({
                message: 'An error occurred while applying credit.',
                placement: 'bottom',
            });
        }
    };

    const handleRemoveCredit = () => {
        removeCredit();
        notification.success({
            message: 'Credit removed successfully',
            placement: 'bottom',
        });
    };

    return (
        <div style={{ background: '#fff' }}>
            <CreditBannerWrapper style={{ borderRadius: 0, marginBottom: 0 }}>
                <CreditBannerContent>
                    <CreditBalanceLabel>Credit balance</CreditBalanceLabel>
                    <CreditBalanceAmount>
                          {formatNumberAsCurrency(b2CUser?.WalletBalance || 0, "")}
                    </CreditBalanceAmount>
                </CreditBannerContent>

                <CreditBannerBackground>
                    <img src={CreditBalIcon} alt="" />
                </CreditBannerBackground>

                <CreditBannerContent>
                    {creditApplied ? (
                        <StyledCreditButton 
                            onClick={handleRemoveCredit} 
                            icon={<DeleteOutlined style={{ color: '#6B7280' }} />}
                        >
                            <span>Remove credit</span>
                        </StyledCreditButton>
                    ) : (
                        <StyledCreditButton 
                            onClick={handleApplyCredit} 
                            disabled={promoCodeEntered || creditLoading}
                            loading={creditLoading}
                        >
                            <span>Apply credit</span>
                        </StyledCreditButton>
                    )}
                </CreditBannerContent>
            </CreditBannerWrapper>
            
            {creditApplied && creditDeducted > 0 && (
                <RedeemCreditAppliedRow>
                    <span>Proceed to redeem credit applied</span>
                    <span>({formatNumberAsCurrency(creditDeducted, "")} off)</span>
                </RedeemCreditAppliedRow>
            )}
        </div>
    );
};

export default MobileCreditBanner;
